<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { VBottomSheet } from 'vuetify/components/VBottomSheet'
import { makeVDialogProps, VDialog } from 'vuetify/lib/components/VDialog/VDialog.mjs'

import { dialogSizes } from './types'
import type { DialogSize } from './types'

import mdiClose from '~icons/mdi/close'

// Generic dialog base (ADA-4437). LOOK & BEHAVIOR come from the legacy ActionDialog
// (rounded sheet on desktop, inset bottom sheet on mobile, toolbar header with a
// close X); the API mirrors ada-app-ui's BDialog where the shapes overlap (hideHeader/
// hideFooter naming, #title/#header-actions/#footer slots with closeProps, defineModel,
// after-leave, size tokens, confirmOnClose) so the stacks stay portable. Deliberate
// customer-ui extensions vs ada-app-ui: the mobile bottom-sheet mode, the headerDivider
// prop, and `size` defaulting to `medium` (app-ui leaves the width unstyled by default);
// fullscreenAction is intentionally not ported, and the title is always bold (design —
// PR #895 review). `height`/`fullscreen` are v-dialog's own props (via makeVDialogProps)
// — e.g. the media viewer passes `height="90vh"` + `:fullscreen="mobile"`.

const props = defineProps({
  hideFooter: {
    type: Boolean,
    default: false,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  headerDivider: {
    type: Boolean,
    default: true,
  },
  ariaLabel: {
    type: String,
    default: undefined,
  },
  size: {
    type: String as () => DialogSize,
    default: 'medium',
    validator: (v: string) => Object.keys(dialogSizes).includes(v),
  },
  /**
   * When true, user-initiated close attempts (ESC, click-outside, header X, footer close
   * button) show a Yes/No confirm before closing. Programmatic `v-model = false` from the
   * parent bypasses this — use it for save flows and other parent-driven close.
   */
  confirmOnClose: {
    type: Boolean,
    default: false,
  },
  confirmOnCloseTitle: {
    type: String,
    default: 'common.confirm_close_dialog_title',
  },
  confirmOnCloseMessage: {
    type: String,
    default: 'common.confirm_close_dialog_message',
  },
  ...makeVDialogProps(),
})
const emit = defineEmits<{
  'after-leave': []
}>()
const model = defineModel<boolean>()

const { t } = useI18n()
const { mobile } = useDisplay()

const id = useId()
const showConfirmClose = ref(false)

// Priority: `fullscreen` (v-dialog prop, passed through) > mobile bottom-sheet > desktop
// dialog. The bottom-sheet mode is the legacy ActionDialog mobile behavior.
const asBottomSheet = computed(() => mobile.value && !props.fullscreen)

// Width resolution: explicit `width` wins, else the `size` token — `size` defaults to
// `medium` (PR #895 review; customer-ui deviation from ada-app-ui, whose BDialog has no
// default width). The 90vw cap lives inside the tokens.
const resolvedWidth = computed(() => props.width ?? dialogSizes[props.size])

// Exclude modelValue from v-dialog's filtered props — we drive it via :model-value below.
const vDialogProps = computed(() => {
  const filtered = { ...VDialog.filterProps(props) } as Record<string, unknown>
  delete filtered.modelValue
  return filtered
})

// One shared content tree — the root component switches between dialog and bottom sheet.
// ⚠️ Bottom-sheet mode forwards ONLY inset + persistent: v-dialog props (width/height/scrollable/…)
// are dropped there by design, since the sheet sizes itself against the viewport. A consumer that
// needs one of them on mobile must either use `fullscreen` (which keeps the v-dialog root) or add
// the prop to `rootProps` below (PR #895 review).
const rootComponent = computed(() => (asBottomSheet.value ? VBottomSheet : VDialog))
const rootProps = computed<Record<string, unknown>>(() =>
  asBottomSheet.value
    ? { inset: true, persistent: props.persistent }
    : { ...vDialogProps.value, width: resolvedWidth.value }
)

// Rounded like the legacy ActionDialog sheet — except fullscreen, where rounded corners
// against the viewport edges look broken.
const sheetRounding = computed(() => {
  if (props.fullscreen) return 'rounded-0'
  return asBottomSheet.value ? 'rounded-t-xl' : 'rounded-xl'
})

// Routes user-initiated close attempts through the confirm logic.
// Programmatic `model = false` from the parent bypasses this — that's intentional.
function requestClose() {
  if (props.confirmOnClose) {
    showConfirmClose.value = true
  } else {
    model.value = false
  }
}

// Vuetify-style spreadable props object — exposed via the #footer slot prop.
// Consumers spread it on their cancel button: `<v-btn v-bind="closeProps">`.
const closeProps = { onClick: requestClose }

function onRootUpdate(val: boolean) {
  // v-dialog/v-bottom-sheet only emit update:model-value=false for ESC / click-outside.
  // Programmatic close from the parent goes through the prop binding silently.
  if (!val) {
    requestClose()
  }
}

function afterLeave() {
  emit('after-leave')
}

function confirmClose() {
  showConfirmClose.value = false
  model.value = false
}

function cancelClose() {
  showConfirmClose.value = false
}
</script>

<template>
  <component
    :is="rootComponent"
    v-bind="rootProps"
    :model-value="model"
    :aria-labelledby="props.hideHeader ? undefined : id"
    :aria-label="
      props.hideHeader && props.ariaLabel != null && props.ariaLabel.trim() !== '' ? props.ariaLabel : undefined
    "
    @update:model-value="onRootUpdate"
    @after-leave="afterLeave"
  >
    <v-sheet
      :class="sheetRounding"
      class="overflow-hidden d-flex flex-column h-100"
    >
      <template v-if="!props.hideHeader">
        <v-toolbar color="transparent">
          <v-toolbar-title
            :id="id"
            class="font-weight-semibold"
          >
            <slot name="title" />
          </v-toolbar-title>

          <slot name="header-actions" />

          <v-btn
            :icon="mdiClose"
            :aria-label="t('common.close')"
            @click="requestClose"
          />
        </v-toolbar>

        <v-divider
          v-if="props.headerDivider"
          class="mb-4"
        />
      </template>

      <div class="flex-grow-1 overflow-y-auto">
        <slot />
      </div>

      <template v-if="!props.hideFooter">
        <v-divider />

        <v-card-actions class="pa-3">
          <slot
            name="footer"
            :close-props="closeProps"
          >
            <v-btn
              color="primary"
              v-bind="closeProps"
            >
              {{ t('common.close') }}
            </v-btn>
          </slot>
        </v-card-actions>
      </template>
    </v-sheet>
  </component>

  <v-dialog
    v-model="showConfirmClose"
    persistent
    :width="dialogSizes.small"
    :aria-labelledby="`${id}-confirm`"
  >
    <v-card>
      <v-card-title
        :id="`${id}-confirm`"
        class="pa-4"
      >
        {{ t(props.confirmOnCloseTitle) }}
      </v-card-title>

      <v-divider />

      <v-card-text>
        {{ t(props.confirmOnCloseMessage) }}
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3">
        <v-spacer />

        <v-btn
          variant="text"
          color="primary"
          @click="cancelClose"
        >
          {{ t('common.cancel') }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          @click="confirmClose"
        >
          {{ t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
